import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import {
  BreakpointObserverService,
  HeaderMenuService,
  HREF,
  ScriptLoaderService,
  SessionService,
  SITE_TITLE,
} from '@core';
import { INavigationMenuAction, NavigationLinksComponent, NavigationMenuComponent } from '@navigation';
import {
  AccessibleClickDirective,
  BottomFooterComponent,
  BrandsListComponent,
  CookiePolicyComponent,
  FocusOutsideDirective,
  FooterComponent,
  HideHeaderWithHeightDirective,
  SearchPanelComponent,
  SnackbarComponent,
  SnackbarService,
  TopHeaderComponent,
} from '@shared';
import { filter, map, tap } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Title } from '@angular/platform-browser';
import { NavigationMenuService } from 'libs/ui/navigation/src/lib/navigation/components/navigation-menu-mobile/navigation-menu.service';
import { AdjustContentTopPaddingDirective } from './adjust-content-top-padding.directive';
import { MICROFORM_URL } from './checkout/components/checkout-payment-options-form/constants';

@Component({
  selector: 'app-pages',
  templateUrl: './pages.component.html',
  styleUrls: ['./pages.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    FooterComponent,
    NavigationMenuComponent,
    NavigationLinksComponent,
    RouterModule,
    MatIconModule,
    MatToolbarModule,
    MatProgressSpinnerModule,
    MatSidenavModule,
    TopHeaderComponent,
    BrandsListComponent,
    BottomFooterComponent,
    SearchPanelComponent,
    HideHeaderWithHeightDirective,
    SnackbarComponent,
    FocusOutsideDirective,
    AccessibleClickDirective,
    AdjustContentTopPaddingDirective,
    CookiePolicyComponent,
  ],
})
export class PagesComponent implements OnInit, AfterViewInit {
  private readonly navigationMenuService = inject(NavigationMenuService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly menuService = inject(HeaderMenuService);
  private readonly bannerService = inject(SnackbarService);
  private readonly router = inject(Router);
  private readonly sessionService = inject(SessionService);
  private readonly titleService = inject(Title);
  private readonly scriptLoaderService = inject(ScriptLoaderService);

  private readonly isMobileAgent = inject(BreakpointObserverService).isMobileAgent;
  isMobile = inject(BreakpointObserverService).isMobile;
  isTabletOrLower = inject(BreakpointObserverService).isTabletOrLower;
  isHeaderHidden = signal(false);
  readonly navigationTree = this.menuService.categoriesView;
  readonly brands = this.menuService.brandsView;
  readonly navigationMenuActions = signal<INavigationMenuAction[]>([]);

  bannerShown = computed(() => this.bannerService.bannerShown());
  hideNavHeader = toSignal(
    this.router.events.pipe(
      map(() => this.router.url?.includes(HREF.CHECKOUT)),
      takeUntilDestroyed(this.destroyRef),
    ),
  );

  private readonly categoriesActionTemplate = viewChild<TemplateRef<unknown>>('categoriesActionTemplate');
  private readonly brandActionTemplate = viewChild<TemplateRef<unknown>>('brandActionTemplate');
  private readonly searchActionTemplate = viewChild<TemplateRef<unknown>>('searchActionTemplate');
  private readonly navigationMenu = viewChild<NavigationMenuComponent>('navigationMenu');
  private readonly searchPanel = viewChild<SearchPanelComponent>('searchPanel');
  private readonly categoriesPanel = viewChild<NavigationLinksComponent>('categoriesPanel');
  private readonly brandsPanel = viewChild<BrandsListComponent>('brandsPanel');
  private readonly $categoriesPanelObservable = toObservable(this.categoriesPanel);
  private readonly $brandsPanelObservable = toObservable(this.brandsPanel);

  ngOnInit() {
    this.scriptLoaderService.loadScript(MICROFORM_URL);

    this.setupNavigationMenuSubscriptions();
    this.sessionService.setupSessionTimeoutFeature();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.checkUrlIsMarketPlace(this.router.url)),
      )
      .subscribe(() => {
        this.titleService.setTitle(this.isMobile() || this.isMobileAgent() ? SITE_TITLE.MOBILE : SITE_TITLE.DEFAULT);
      });
  }

  ngAfterViewInit() {
    this.setNavigationMenuActions();
  }

  closeNavigationMenu() {
    this.navigationMenu()?.closeMenu();
  }

  private setupNavigationMenuSubscriptions(): void {
    //close menu
    this.navigationMenuService.closeEvent$
      .pipe(
        tap(() => this.closeNavigationMenu()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private setNavigationMenuActions(): void {
    this.navigationMenuActions.set([
      {
        title: 'Category',
        icon: 'ico_category',
        tempRef: this.categoriesActionTemplate(),
        onSelect: () => {
          this.$categoriesPanelObservable
            .pipe(
              map((item) => item?.menuItem()?.[0]?.nativeElement),
              filter(Boolean),
              takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((item) => item?.focus());
        },
      },
      {
        title: 'Brand',
        icon: 'ico_tag',
        tempRef: this.brandActionTemplate(),
        onSelect: () => {
          this.$brandsPanelObservable
            .pipe(
              map((item) => item?.containerRef().nativeElement?.querySelector('button')),
              filter(Boolean),
              takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((item) => item.focus());
        },
      },
      {
        title: 'Search',
        icon: 'ico_search',
        tempRef: this.searchActionTemplate(),
        onSelect: () => this.searchPanel()?.focus(),
        isHideMobileTitle: true,
      },
    ]);
  }

  goHome() {
    this.router.navigate(['']);
  }

  headerHidden(event: boolean | Event): void {
    this.isHeaderHidden.set(event as boolean);
  }

  private checkUrlIsMarketPlace(url: string): boolean {
    const tmpUrl = new URL(`${window.location.origin}${url}`);
    const { pathname } = tmpUrl;
    return [pathname, url].includes(`/${HREF.HOME}`);
  }
}
